import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Search, X } from "lucide-react";

const idify = (s) => s.replace(/[^a-z0-9]/gi, "-").toLowerCase();

const ProductGrid = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
    {items.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
  </div>
);

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]); // ordered, without "All"
  const [loading, setLoading] = useState(true);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [query, setQuery] = useState("");
  const active = params.get("category") || "All";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = active && active !== "All" ? `/products?category=${encodeURIComponent(active)}` : "/products";
    api.get(url).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [active]);

  const setCat = (c) => {
    if (c === "All") setParams({});
    else setParams({ category: c });
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (inStockOnly && p.in_stock === false) return false;
      if (q) {
        const hay = `${p.name} ${p.short || ""} ${p.category || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [products, inStockOnly, query]);

  // group by category, in the catalog's defined order (used in the "All" view)
  const groups = useMemo(
    () =>
      categories
        .map((cat) => ({ cat, items: visible.filter((p) => p.category === cat) }))
        .filter((g) => g.items.length > 0),
    [categories, visible]
  );

  const filterTabs = ["All", ...categories];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
      <Seo title={active && active !== "All" ? active : "Shop"} path="/shop"
        description="Browse liquid mushroom cultures, all-in-one grow bags, 5-gallon bucket kits, and cultivation ebooks from NC Mycology." />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
        <p className="font-mono-label text-xs text-primary mb-3">The Shop</p>
        <h1 className="font-display text-4xl sm:text-6xl font-light mb-8">Everything to grow</h1>
      </motion.div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="shop-search-input"
          placeholder="Search cultures, grow bags, guides…"
          className="w-full bg-card border border-border rounded-full pl-11 pr-10 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            data-testid="shop-search-clear"
            aria-label="Clear search"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-12 border-b border-border pb-6">
        {filterTabs.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`filter-${idify(c)}`}
            className={`font-mono-label text-[11px] px-4 py-2 rounded-full border transition-colors ${
              active === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary"
            }`}>
            {c}
          </button>
        ))}
        <button onClick={() => setInStockOnly((v) => !v)} data-testid="filter-in-stock-toggle" aria-pressed={inStockOnly}
          className={`ml-auto inline-flex items-center gap-2 font-mono-label text-[11px] px-4 py-2 rounded-full border transition-colors ${
            inStockOnly ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary"
          }`}>
          <span className={`w-3.5 h-3.5 rounded-[3px] border flex items-center justify-center ${inStockOnly ? "border-primary-foreground" : "border-current"}`}>
            {inStockOnly && <Check className="w-2.5 h-2.5" />}
          </span>
          In stock only
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] w-full" />)}
        </div>
      ) : active === "All" ? (
        <div className="space-y-16" data-testid="product-grid">
          {groups.map((g) => {
            const showAll = query.trim().length > 0;
            const shown = showAll ? g.items : g.items.slice(0, 4);
            const hasMore = g.items.length > shown.length;
            return (
            <section key={g.cat} data-testid={`category-section-${idify(g.cat)}`}>
              <div className="flex items-baseline justify-between mb-6 gap-4">
                <h2 className="font-display text-2xl sm:text-3xl font-light">{g.cat}</h2>
                {hasMore && (
                  <button onClick={() => setCat(g.cat)} data-testid={`view-all-${idify(g.cat)}`}
                    className="font-mono-label text-[11px] text-primary hover:underline whitespace-nowrap">
                    View all {g.items.length} &rarr;
                  </button>
                )}
              </div>
              <ProductGrid items={shown} />
            </section>
            );
          })}
        </div>
      ) : (
        <div data-testid="product-grid">
          <ProductGrid items={visible} />
        </div>
      )}

      {!loading && visible.length === 0 && (
        <p className="text-muted-foreground py-20 text-center" data-testid="shop-empty">
          {query.trim()
            ? `No products match "${query.trim()}".`
            : inStockOnly
            ? "Nothing in stock in this category right now."
            : "No products in this category yet."}
        </p>
      )}
    </div>
  );
}
